import { useState } from 'react';
import { pressArticles } from '@/mocks/press';

const typeLabel: Record<string, string> = {
  interview: 'Interview',
  feature: 'Feature',
  review: 'Review',
  mention: 'Mention',
  'african-voices': 'African Voices Changemakers Feature',
};

const typeBadgeColor: Record<string, string> = {
  interview: 'bg-amber-100 text-amber-800',
  feature: 'bg-stone-100 text-stone-700',
  review: 'bg-emerald-100 text-emerald-800',
  mention: 'bg-rose-100 text-rose-700',
  'african-voices': 'bg-amber-50 text-amber-900',
};

interface ArticleCardProps {
  article: (typeof pressArticles)[number];
}

function ArticleCard({ article }: ArticleCardProps) {
  const [imgSrc, setImgSrc] = useState(article.coverImage ?? article.coverFallback ?? '');

  const handleError = () => {
    if (article.coverFallback && imgSrc !== article.coverFallback) {
      setImgSrc(article.coverFallback);
    }
  };

  return (
    <article
      itemScope
      itemType="https://schema.org/NewsArticle"
      className="bg-white rounded-lg flex flex-col"
    >
      <meta itemProp="headline" content={article.title} />
      <meta itemProp="datePublished" content={article.year} />
      <link itemProp="publisher" href={article.url} />
      <meta itemProp="author" content={article.outlet} />
      <meta itemProp="about" content="Oludamola Adebowale" />

      {/* Cover image */}
      {imgSrc && (
        <div className="w-full flex-shrink-0 bg-stone-100 sm:aspect-[4/3] sm:overflow-hidden sm:rounded-t-lg">
          <img
            src={imgSrc}
            alt={article.title}
            onError={handleError}
            className="w-full h-auto rounded-t-lg sm:h-full sm:object-cover sm:object-top sm:rounded-none"
            itemProp="image"
          />
        </div>
      )}

      <div className="p-5 flex flex-col flex-1">
        {/* Outlet + type row */}
        <div className="flex items-center justify-between mb-3">
          <span className="font-sans text-xs font-bold uppercase tracking-wider text-gray-800">
            {article.outlet}
          </span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeBadgeColor[article.type] ?? 'bg-gray-100 text-gray-600'}`}>
            {typeLabel[article.type]}
          </span>
        </div>

        <p className="text-xs text-gray-400 font-sans mb-2">{article.year}</p>
        <h3 className="font-serif text-base font-semibold text-gray-900 mb-3 leading-snug flex-1">
          {article.title}
        </h3>
        <p className="font-sans text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
          {article.excerpt}
        </p>
        <a
          href={article.url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="inline-flex items-center gap-1 font-sans text-xs uppercase tracking-wider text-[#b89a6a] hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
        >
          Read Article <i className="ri-external-link-line" />
        </a>
      </div>
    </article>
  );
}

export default function PressCoverage() {
  return (
    <section
      className="bg-[#f9f6f1] py-20"
      itemScope
      itemType="https://schema.org/WebPage"
    >
      <meta itemProp="name" content="Press Coverage: Oludamola Adebowale" />
      <meta itemProp="description" content="Media coverage of Oludamola Adebowale documenting his curatorial exhibitions and archival work." />
      <link itemProp="mainEntity" href="https://oludamolaadebowale.com/#person" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">IN THE MEDIA</p>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Press Coverage
          </h2>
          <p className="font-sans text-base text-gray-600 max-w-2xl">
            Selected coverage from media outlets documenting Oludamola Adebowale&apos;s contributions to cultural heritage and archival practice.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pressArticles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      </div>
    </section>
  );
}
